/**
 * Instruction checkbox handler
 *
 * Adds checkboxes before each instruction step and handles
 * checked state with strikethrough styling.
 */

/**
 * Create a checkbox element for an instruction step
 * @param {string} stepKey - Key in format "step_{stepIndex}"
 * @param {boolean} isChecked - Whether the checkbox should be checked
 * @param {Function} onToggle - Callback when checkbox is toggled
 * @return {HTMLElement}
 */
const createCheckbox = (stepKey, isChecked, onToggle) => {
  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.className = 'mv-create-instruction-checkbox'
  checkbox.checked = isChecked
  checkbox.setAttribute('data-step-key', stepKey)
  checkbox.setAttribute('aria-label', 'Mark step as completed')

  checkbox.addEventListener('change', (e) => {
    onToggle(stepKey)
    updateStepStyle(e.target.closest('li'), e.target.checked)
  })

  return checkbox
}

/**
 * Update the visual style of an instruction step based on checked state
 * @param {HTMLElement} listItem - The li element
 * @param {boolean} isChecked - Whether the step is checked
 */
const updateStepStyle = (listItem, isChecked) => {
  if (!listItem) return

  if (isChecked) {
    listItem.classList.add('mv-checklist-checked')
  } else {
    listItem.classList.remove('mv-checklist-checked')
  }
}

/**
 * Initialize instruction checkboxes for a card
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isStepChecked - Function to check if a step is checked
 * @param {Function} toggleStep - Function to toggle a step's state
 */
export const initInstructionCheckboxes = (
  cardElement,
  isStepChecked,
  toggleStep
) => {
  // Find all instruction steps with data-step-index attribute
  const instructionSteps = cardElement.querySelectorAll(
    '.mv-create-instructions li[data-step-index]'
  )

  instructionSteps.forEach((step) => {
    const stepIndex = step.getAttribute('data-step-index')
    const stepKey = `step_${stepIndex}`

    // Check if checkbox already exists (avoid duplicates)
    if (step.querySelector('.mv-create-instruction-checkbox')) {
      return
    }

    // Get current checked state
    const isChecked = isStepChecked(stepKey)

    // Create and insert checkbox
    const checkbox = createCheckbox(stepKey, isChecked, toggleStep)
    step.insertBefore(checkbox, step.firstChild)

    // Apply initial style if checked
    if (isChecked) {
      updateStepStyle(step, true)
    }
  })
}

/**
 * Restore instruction checkbox states from saved state
 * @param {HTMLElement} cardElement - The Create card container element
 * @param {Function} isStepChecked - Function to check if a step is checked
 */
export const restoreInstructionStates = (cardElement, isStepChecked) => {
  const checkboxes = cardElement.querySelectorAll('.mv-create-instruction-checkbox')

  checkboxes.forEach((checkbox) => {
    const stepKey = checkbox.getAttribute('data-step-key')
    const isChecked = isStepChecked(stepKey)

    checkbox.checked = isChecked
    updateStepStyle(checkbox.closest('li'), isChecked)
  })
}

export default initInstructionCheckboxes
